import { makeStyles } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import {
  STYLE_HL_INCOMPLETE_REGISTRATION,
  STYLE_HL_REGISTERED,
  STYLE_HL_REGISTERED_DARKER,
  STYLE_HL_UNREGISTERED,
  useTemplateStyles
} from "@saleor/dropOff/styles/templates";
import * as React from "react";
import { useState } from "react";

import DropOffFilterSelect from "../DropOffFilterSelect";

const useStyles = makeStyles(
  _ => ({
    hoverLabel: {
      fontSize: "11px",
      fontWeight: 500,
      margin: "5px"
    },
    itemIconSvg: {
      color: "#0030ff",
      fontSize: "1rem"
    },
    itemIconSvgCircle: {
      alignItems: "center",
      borderRadius: "50%",
      display: "inline-flex",
      justifyContent: "center"
    },
    tooltip: {
      "&::after": {
        borderColor: "transparent transparent white transparent",
        borderStyle: "solid",
        borderWidth: "8px",
        bottom: "100%",
        content: '""',
        left: "50%",
        position: "absolute",
        transform: "translateX(-50%)"
      },
      backgroundColor: "white",
      borderRadius: "5px",
      boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
      color: "black",
      fontSize: "14px",
      left: "50%",
      marginTop: "5px",
      padding: "5px 15px",
      position: "absolute",
      textAlign: "left",
      transform: "translateX(-50%)",
      visibility: "hidden",
      width: "120px",
      zIndex: 1
    },
    tooltipContainer: {
      display: "inline-block",
      marginTop: "5px",
      position: "relative"
    },
    tooltipVisible: {
      visibility: "visible"
    }
  }),
  { name: "DropOffHomeLocLabels" }
);

export enum DropOffHomeLocOutletIndicatorEnum {
  UNREGISTERED = 1,
  REGISTERED = 3,
  INCOMPLETE = 2
}

export interface DropOffHomeLocLabelsProps {
  filterOutletStatus: number;
  setFilterOutletStatus: React.Dispatch<React.SetStateAction<number>>;
}
const DropOffHomeLocLabels: React.FC<DropOffHomeLocLabelsProps> = props => {
  const { filterOutletStatus, setFilterOutletStatus } = props;
  const classes = useTemplateStyles({});

  return (
    <div
      className={`
            ${classes.templateTitle}
            ${classes.dropOffTabs}
            ${classes.templateTitleLabelContainer}
          `}
    >
      <div className={classes.templateTitleAvailable}>
        <h4 style={{ color: "#747C8C" }}>Home Location Status:</h4>
      </div>
      <div className={classes.templateTitleLabels}>
        <DropOffHomeLocIndicator
          status={DropOffHomeLocOutletIndicatorEnum.UNREGISTERED}
        />
        <span className={classes.iconLabel}>Unregistered</span>

        <DropOffHomeLocIndicator
          status={DropOffHomeLocOutletIndicatorEnum.INCOMPLETE}
        />
        <span className={classes.iconLabel}>Incomplete</span>

        <DropOffHomeLocIndicator
          status={DropOffHomeLocOutletIndicatorEnum.REGISTERED}
        />
        <span className={classes.iconLabelEnd}>Registered</span>
      </div>
      <div className={classes.templateTitleFilter}>
        <h4 style={{ color: "#747C8C" }}>Show:</h4>
        <DropOffFilterSelect
          filterOutletStatus={filterOutletStatus}
          setFilterOutletStatus={setFilterOutletStatus}
        />
      </div>
    </div>
  );
};
export default DropOffHomeLocLabels;

export interface DropOffHomeLocLabelTooltipProps {
  triggerComponent: JSX.Element;
  ctrRegistered?: string;
  ctrUnregistered?: string;
  ctrIncomplete?: string;
}
export const DropOffHomeLocLabelTooltip: React.FC<DropOffHomeLocLabelTooltipProps> = props => {
  const {
    triggerComponent,
    ctrRegistered,
    ctrUnregistered,
    ctrIncomplete
  } = props;
  const classes = useStyles({});
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={classes.tooltipContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={
        ctrRegistered || ctrUnregistered || ctrIncomplete
          ? { cursor: "pointer" }
          : {}
      }
    >
      {triggerComponent}
      <div
        className={`${classes.tooltip} ${
          isHovered && (ctrRegistered || ctrUnregistered || ctrIncomplete)
            ? classes.tooltipVisible
            : ""
        }`}
      >
        {ctrUnregistered && (
          <p className={classes.hoverLabel}>Unregistered {ctrUnregistered}</p>
        )}
        {ctrRegistered && (
          <p className={classes.hoverLabel}>Registered {ctrRegistered}</p>
        )}
        {ctrIncomplete && (
          <p className={classes.hoverLabel}>Incomplete {ctrIncomplete}</p>
        )}
      </div>
    </div>
  );
};

export interface DropOffHomeLocIndicatorProps {
  status: DropOffHomeLocOutletIndicatorEnum;
  className?: string;
  ctrRegistered?: string;
  ctrUnregistered?: string;
  ctrIncomplete?: string;
  useDarkerShade?: true;
}
export const DropOffHomeLocIndicator: React.FC<DropOffHomeLocIndicatorProps> = props => {
  const {
    status,
    className,
    ctrRegistered,
    ctrUnregistered,
    ctrIncomplete,
    useDarkerShade
  } = props;
  const classes = useStyles(props);

  const getHomeLocIndicators = (
    homeLocOutletIndicator: DropOffHomeLocOutletIndicatorEnum
  ) => {
    if (
      homeLocOutletIndicator === DropOffHomeLocOutletIndicatorEnum.REGISTERED
    ) {
      return (
        <CheckCircleIcon
          className={`${classes.itemIconSvg} ${classes.itemIconSvgCircle} ${className}`}
          style={
            useDarkerShade ? STYLE_HL_REGISTERED_DARKER : STYLE_HL_REGISTERED
          }
        />
      );
    } else if (
      homeLocOutletIndicator === DropOffHomeLocOutletIndicatorEnum.INCOMPLETE
    ) {
      return (
        <WatchLaterIcon
          className={`${classes.itemIconSvg} ${classes.itemIconSvgCircle} ${className}`}
          style={STYLE_HL_INCOMPLETE_REGISTRATION}
        />
      );
    } else {
      return (
        <ErrorIcon
          className={`${classes.itemIconSvg} ${classes.itemIconSvgCircle} ${className}`}
          style={STYLE_HL_UNREGISTERED}
        />
      );
    }
  };

  return (
    <DropOffHomeLocLabelTooltip
      triggerComponent={getHomeLocIndicators(status)}
      ctrRegistered={ctrRegistered}
      ctrUnregistered={ctrUnregistered}
      ctrIncomplete={ctrIncomplete}
    />
  );
};
